/**
 * Task #1480 — bozze persistenti (server-side) del Carico Merce.
 *
 * Sostituisce l'autosave su localStorage del modal: la bozza viaggia per
 * tenant + utente proprietario + opzionale `ddt_passivo_id`/`purchase_order_id`,
 * così l'operatore può sospendere/riprendere da qualsiasi device.
 */

import { z } from 'zod';
import { LineCompletenessOriginSchema } from './line-completeness';

/** Header opaco (campi documento: data, fornitore, magazzino, riferimenti). */
export const GoodsReceiptDraftHeaderSchema = z.record(z.string(), z.unknown());
/** Lines opache: il modal le riconosce al replay. */
export const GoodsReceiptDraftLineSchema = z.record(z.string(), z.unknown());
/** Source: tracciamento provenienza AI/OCR (confidenze, bbox, ocr_header). */
export const GoodsReceiptDraftSourceSchema = z.object({
  ocr_header: z.record(z.string(), z.unknown()).optional(),
  ai_field_confidence: z.record(z.string(), z.record(z.string(), z.number())).optional(),
  /** DDT passivi selezionati per un unico carico combinato. */
  ddt_ids: z.array(z.string().uuid()).max(1000).optional(),
  bbox: z.record(z.string(), z.unknown()).optional(),
}).passthrough();

export type GoodsReceiptDraftHeader = z.infer<typeof GoodsReceiptDraftHeaderSchema>;
export type GoodsReceiptDraftLine = z.infer<typeof GoodsReceiptDraftLineSchema>;
export type GoodsReceiptDraftSource = z.infer<typeof GoodsReceiptDraftSourceSchema>;

/** Payload aggregato (back-compat per il modal): proietta header+lines+source. */
export const GoodsReceiptDraftPayloadSchema = z.object({
  origin: LineCompletenessOriginSchema,
  header: GoodsReceiptDraftHeaderSchema,
  lines: z.array(GoodsReceiptDraftLineSchema),
  /**
   * Selezione dell'editor; la provenienza fisica finalizzata resta canonica
   * nelle allocazioni per-riga.
   */
  ddt_ids: z.array(z.string().uuid()).max(1000).refine(
    ids => new Set(ids).size === ids.length,
    'I DDT selezionati devono essere univoci',
  ).optional(),
  ocrHeader: z.record(z.string(), z.unknown()).optional(),
  ai_field_confidence: z.record(z.string(), z.record(z.string(), z.number())).optional(),
  schema_version: z.literal(1),
});

export type GoodsReceiptDraftPayload = z.infer<typeof GoodsReceiptDraftPayloadSchema>;

export interface GoodsReceiptDraft {
  id: string;
  tenant_id: string;
  created_by: string;
  ddt_passivo_id: string | null;
  purchase_order_id: string | null;
  warehouse_id: string | null;
  supplier_name: string | null;
  origin: GoodsReceiptDraftPayload['origin'];
  /** Storage canonico — header opaco. */
  header: GoodsReceiptDraftHeader;
  /** Storage canonico — array di righe opache. */
  lines: GoodsReceiptDraftLine[];
  /** Storage canonico — provenienza AI/OCR (opzionale). */
  source: GoodsReceiptDraftSource | null;
  /** Riferimento esterno univoco (es: hash file OCR, id PO origine). */
  source_ref_id: string | null;
  /** URL file sorgente (PDF/immagine) per source preview pane. */
  source_file_url: string | null;
  schema_version: number;
  /** Proiezione back-compat: payload = { origin, header, lines, ocrHeader?, ai_field_confidence?, schema_version }. */
  payload: GoodsReceiptDraftPayload;
  lines_total: number;
  lines_complete: number;
  lines_incomplete: number;
  created_at: string;
  updated_at: string;
}

// NB: dalla migration `20261015000000_ddt_passivi_id_uuid.sql` gli id dei DDT
// passivi (`pos_ddt_passivi.id`, FK `pos_goods_receipt_drafts.ddt_passivo_id`)
// sono uuid nativi: il vincolo `.uuid()` è corretto e fa fail-fast in
// validazione invece che con un errore DB a runtime.
// `source_file_url` può essere un path R2 relativo o un URL: validiamo come
// stringa lunga max 2048, l'URL strict si fa lato preview UI quando serve.
const GoodsReceiptDraftWriteSchema = z.object({
  ddt_passivo_id: z.string().uuid().nullable().optional(),
  purchase_order_id: z.string().uuid().nullable().optional(),
  warehouse_id: z.string().uuid().nullable().optional(),
  supplier_name: z.string().max(255).nullable().optional(),
  source_ref_id: z.string().max(512).nullable().optional(),
  source_file_url: z.string().max(2048).nullable().optional(),
  payload: GoodsReceiptDraftPayloadSchema,
  lines_total: z.number().int().min(0),
  lines_complete: z.number().int().min(0),
  lines_incomplete: z.number().int().min(0),
});

function validateDraftDdtProjection(
  value: { ddt_passivo_id?: string | null; payload?: GoodsReceiptDraftPayload },
  ctx: z.RefinementCtx,
): void {
  const ids = value.payload?.ddt_ids;
  if (!ids) return;
  const expectedScalar = ids.length === 1 ? ids[0] : null;
  if (value.ddt_passivo_id !== undefined && value.ddt_passivo_id !== expectedScalar) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'ddt_passivo_id deve valorizzare l’unico DDT selezionato ed essere null per una selezione multipla.',
      path: ['ddt_passivo_id'],
    });
  }
}

export const GoodsReceiptDraftCreateSchema = GoodsReceiptDraftWriteSchema.superRefine(validateDraftDdtProjection);

export const GoodsReceiptDraftUpdateSchema = GoodsReceiptDraftWriteSchema.partial().superRefine(validateDraftDdtProjection);

export type GoodsReceiptDraftCreate = z.infer<typeof GoodsReceiptDraftCreateSchema>;
export type GoodsReceiptDraftUpdate = z.infer<typeof GoodsReceiptDraftUpdateSchema>;
